import React from 'react';
import { Shield, FileText, Scale, Gavel, ArrowLeft, UserPlus, Activity, CreditCard } from 'lucide-react';

interface TermsOfServiceProps {
  onBack?: () => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-black text-white p-8 lg:p-20 animate-fade-in">
      <div className="max-w-4xl mx-auto space-y-12">
        {onBack && (
          <button 
            onClick={onBack}
            className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 mb-8"
          >
            <ArrowLeft className="w-3 h-3" /> Voltar
          </button>
        )}

        <div className="space-y-4">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400">
            <Shield className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Segurança Jurídica</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tighter">Termos de Uso e Contrato de Prestação de Serviços</h1>
          <p className="text-slate-500 font-medium">Última atualização: 26 de Março de 2026</p>
        </div>

        <div className="premium-card p-10 space-y-10 text-slate-300 leading-relaxed text-sm">
          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <FileText className="w-5 h-5 text-indigo-500" />
              1. OBJETO E ACEITAÇÃO
            </h2>
            <p>
              O presente instrumento estabelece os termos e condições para o uso da plataforma <strong>OptiVelo</strong>, um software como serviço (SaaS) de otimização de conversão (CRO) provido pela OptiVelo. Ao utilizar a plataforma, o CLIENTE declara ter lido, compreendido e aceitado integralmente estes termos.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Scale className="w-5 h-5 text-indigo-500" />
              2. RESPONSABILIDADES DA OPTIVELO
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Disponibilidade:</strong> Manter a plataforma disponível 24/7, ressalvadas manutenções programadas ou falhas técnicas de terceiros (servidores de nuvem).</li>
              <li><strong>Segurança de Dados:</strong> Implementar medidas técnicas e organizacionais para proteger os dados do CLIENTE, em conformidade com a LGPD (Lei 13.709/2018).</li>
              <li><strong>Suporte:</strong> Fornecer suporte técnico via VeloBot 24/7 e, subsidiariamente, via equipe humana conforme o plano contratado.</li>
              <li><strong>Atualizações:</strong> Promover melhorias constantes na inteligência artificial e ferramentas de otimização.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Activity className="w-5 h-5 text-indigo-500" />
              3. DISPONIBILIDADE E SLA (SERVICE LEVEL AGREEMENT)
            </h2>
            <p>
              Para os assinantes do plano <strong>Enterprise</strong>, a OptiVelo estabelece uma meta de disponibilidade mensal de <strong>99,9%</strong>. 
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Cálculo:</strong> A disponibilidade é calculada mensalmente, excluindo-se janelas de manutenção programada comunicadas com antecedência mínima de 24h.</li>
              <li><strong>Créditos de Serviço:</strong> Caso a disponibilidade caia abaixo de 99,9% em um determinado mês, o CLIENTE poderá solicitar um crédito de serviço equivalente a 10% da mensalidade daquele mês, a ser descontado na fatura seguinte.</li>
              <li><strong>Exclusões:</strong> Não contam para o cálculo de SLA falhas decorrentes de atos do CLIENTE, problemas na conexão de internet do CLIENTE ou indisponibilidade global de provedores de infraestrutura (ex: Google Cloud, AWS).</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-indigo-500" />
              4. RESPONSABILIDADES DO CLIENTE
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Uso Ético:</strong> Não utilizar a plataforma para criar páginas de conteúdo ilícito, enganoso, fraudulento ou que viole direitos de terceiros.</li>
              <li><strong>Segurança da Conta:</strong> Manter o sigilo de suas credenciais de acesso, sendo o único responsável por qualquer atividade realizada em sua conta.</li>
              <li><strong>Dados de Tráfego:</strong> Garantir que possui autorização legal para coletar e processar dados de seus visitantes através das ferramentas da OptiVelo.</li>
              <li><strong>Pagamento:</strong> Manter a assinatura ativa e em dia para garantir a continuidade do serviço.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Activity className="w-5 h-5 text-indigo-500" />
              5. LIMITAÇÃO DE RESPONSABILIDADE (BLINDAGEM)
            </h2>
            <p className="bg-white/5 p-6 rounded-2xl border border-white/10 italic">
              "A OptiVelo é uma ferramenta de auxílio e otimização para negócios digitais. O CLIENTE reconhece que as taxas de conversão e resultados financeiros dependem de inúmeros fatores externos (qualidade do produto, oferta, público, tráfego pago, mercado, etc.). Portanto, <strong>a OptiVelo NÃO garante resultados financeiros específicos e não se responsabiliza por prejuízos decorrentes de campanhas de marketing ineficazes.</strong>"
            </p>
            <p>
              A responsabilidade total da OptiVelo por qualquer dano comprovado fica limitada ao valor total pago pelo CLIENTE nos últimos 3 (três) meses de assinatura.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Shield className="w-5 h-5 text-indigo-500" />
              6. PROPRIEDADE INTELECTUAL
            </h2>
            <p>
              Todo o código-fonte, algoritmos de IA, designs e marcas associadas à OptiVelo são de propriedade exclusiva da plataforma. O CLIENTE recebe uma licença de uso temporária, não exclusiva e intransferível, estritamente vinculada à vigência da assinatura.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-indigo-500" />
              7. CANCELAMENTO E GARANTIA
            </h2>
            <p>
              Em conformidade com o Código de Defesa do Consumidor (CDC) e políticas da plataforma, o CLIENTE possui 14 (quatorze) dias de garantia incondicional após a primeira compra. Após este período, o cancelamento interrompe a renovação futura, mas não gera direito a reembolso de períodos já utilizados ou em curso.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Gavel className="w-5 h-5 text-indigo-500" />
              8. FORO E LEGISLAÇÃO
            </h2>
            <p>
              Este contrato é regido pelas leis da República Federativa do Brasil. Para dirimir quaisquer controvérsias oriundas deste instrumento, as partes elegem o <strong>Foro da Comarca de Lauro de Freitas - Bahia</strong>, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
            </p>
          </section>
        </div>

        <div className="text-center pb-20">
          <p className="text-slate-500 text-xs">
            Ao clicar em "Assinar" ou utilizar o OptiVelo, você concorda automaticamente com todos os termos acima.
          </p>
        </div>
      </div>
    </div>
  );
};
